import { inviteToWorkspace, removeWorkspaceMember } from "@/lib/api/endpoints";
import type { WorkspaceRole } from "@/lib/api/types";
import { readActiveCustomerFromDocument } from "@/lib/mapper/active-customer-cookie";

/**
 * Workspace member mutations for the active workspace. The backend enforces
 * OWNER/ADMIN and the "last owner" rule; these wrappers only turn errors into
 * results.
 */
type Result = { ok: true } | { ok: false; error: string };

const WORKSPACE_ROLES: readonly WorkspaceRole[] = ["OWNER", "ADMIN", "MEMBER"];

function activeWorkspace(): string | null {
  return readActiveCustomerFromDocument();
}

async function run(fn: () => Promise<unknown>): Promise<Result> {
  try {
    await fn();
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Action failed" };
  }
}

export async function inviteMember(
  emailRaw: string,
  workspaceRole: WorkspaceRole,
): Promise<Result> {
  const workspaceId = activeWorkspace();
  if (!workspaceId) return { ok: false, error: "No active workspace selected." };
  const email = emailRaw.trim().toLowerCase();
  if (!email || !/.+@.+\..+/.test(email)) return { ok: false, error: "Enter a valid email." };
  if (!WORKSPACE_ROLES.includes(workspaceRole)) return { ok: false, error: "Invalid role." };
  return run(() => inviteToWorkspace(workspaceId, { email, workspaceRole }));
}

export async function removeMember(userId: string): Promise<Result> {
  const workspaceId = activeWorkspace();
  if (!workspaceId) return { ok: false, error: "No active workspace selected." };
  if (!userId) return { ok: false, error: "Missing member." };
  return run(() => removeWorkspaceMember(workspaceId, userId));
}
